import React from "react";
import { Components, defaultUrlTransform } from "react-markdown";
import { HelpArticle } from "../../../hooks/useHelpPanel";

/**
 * Shared react-markdown renderer map for every place the portal renders help
 * markdown (the Help Panel drawer and the Material Guide page).
 *
 * This lives here rather than inside HelpPanel.component.tsx because two
 * surfaces now need it and there is no `@tailwindcss/typography` plugin in this
 * project — `prose` classes do not exist, so the styling has to be explicit on
 * every element. Every rule carries its `dark:` counterpart; the Material Guide
 * print view overrides colors separately via `print:` variants.
 *
 * `remark-gfm` is NOT installed, so tables / strikethrough / task lists are not
 * parsed. Content must stay inside CommonMark.
 */

export const HELP_LINK_PREFIX = "help:";

// #region helpUrlTransform
/**
 * react-markdown strips unknown URL schemes by default, which would eat the
 * `help:ARTICLE_KEY` internal links used to cross-link articles.
 */
export const helpUrlTransform = (url: string): string => {
    if (url.startsWith(HELP_LINK_PREFIX)) return url;
    return defaultUrlTransform(url);
};

// #region buildMarkdownComponents
/**
 * @param onOpenArticle - invoked when an internal `help:` link is clicked. When
 * omitted (e.g. the print view), internal links render as inert text so a
 * printed PDF does not show a dead button.
 */
export const buildMarkdownComponents = (onOpenArticle?: (article: HelpArticle) => void): Components => ({
    h1: ({ children }) => (
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 print:text-black">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-2 print:text-black">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-1 print:text-black">{children}</h3>
    ),
    p: ({ children }) => (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed print:text-black">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed print:text-black">{children}</li>
    ),
    strong: ({ children }) => (
        <strong className="font-semibold text-gray-900 dark:text-gray-100 print:text-black">{children}</strong>
    ),
    blockquote: ({ children }) => (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-500 px-4 py-3 mb-3 rounded-r-md print:bg-white print:border-gray-400">
            {children}
        </div>
    ),
    code: ({ children }) => (
        <code className="text-xs bg-surface-muted dark:bg-stone-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded print:bg-white print:text-black">{children}</code>
    ),
    a: ({ children, href }) => {
        if (href?.startsWith(HELP_LINK_PREFIX)) {
            const articleKey = href.slice(HELP_LINK_PREFIX.length);
            const article = HelpArticle[articleKey as keyof typeof HelpArticle];
            if (article && onOpenArticle) {
                return (
                    <button
                        type="button"
                        onClick={() => onOpenArticle(article)}
                        className="text-emerald-600 dark:text-emerald-400 underline cursor-pointer"
                    >
                        {children}
                    </button>
                );
            }
            // Unknown key, or no handler (print view): render inert text rather
            // than a dead link.
            return <span className="font-semibold text-gray-900 dark:text-gray-100 print:text-black">{children}</span>;
        }
        return (
            <a href={href} className="text-emerald-600 dark:text-emerald-400 underline print:text-black" target="_blank" rel="noopener noreferrer">{children}</a>
        );
    },
});
